<template>
    <div class="c-number-input" :class="{
    'c-number-input--inline': inline,
    'c-number-input--center': center,
    'c-number-input--controls': controls
  }" v-on="listeners">
        <label :for="inputId" class="c-number-input__placeholder" v-if="placeholder">
          {{ placeholder }}
          <i v-if="infoIconText.length" class="u-fa u-fa-info departure-children-info-icon" aria-hidden="true" data-bs-toggle="popover" data-bs-trigger="hover" :data-bs-content="`${infoIconText}`"></i>
        </label>
        <div class="c-number-input__inner">
            <button v-if="controls" class="c-number-input__button c-number-input__button--minus" type="button"
                    @click="decrease" :disabled="disabled || readonly || !decreasable"></button>
            <input :id="inputId" class="c-number-input__input" ref="input" type="number" :name="name" :value="currentValue" :min="min"
                   :max="max" :step="step" @change="change" @paste="paste" :readonly="readonly || !inputable"
                   :disabled="disabled || (!decreasable && !increasable)" autocomplete="off">
            <button v-if="controls" class="c-number-input__button c-number-input__button--plus" type="button"
                    @click="increase" :disabled="disabled || readonly || !increasable"></button>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import { uid } from '../helpers/utils';
import BaseComponent from './Base';

const REGEXP_NUMBER = /^-?(?:\d+|\d+\.\d+|\.\d+)(?:[eE][-+]?\d+)?$/;

    @Component({
        model: {
            event: 'change',
        },
    })
    export default class NumberInput extends BaseComponent {
        @Prop({ type: String, default: () => 'vni_' + uid() }) protected readonly inputId: string;
        @Prop({ type: Boolean, default: false }) protected readonly center: boolean;
        @Prop({ type: Boolean, default: true }) protected readonly controls: boolean;
        @Prop({ type: Boolean, default: false }) protected readonly disabled: boolean;
        @Prop({ type: Boolean, default: true }) protected readonly inputable: boolean;
        @Prop({ type: Boolean, default: false }) protected readonly rounded: boolean;
        @Prop({ type: Number, default: Infinity }) protected readonly max: number;
        @Prop({ type: Number, default: -Infinity }) protected readonly min: number;
        @Prop({ type: Number, default: 1 }) protected readonly step: number;
        @Prop(Boolean) protected readonly inline: boolean;
        @Prop(String) protected readonly name: string;
        @Prop(String) protected readonly placeholder: string;
        @Prop(Boolean) protected readonly readonly: boolean;
        @Prop(Number) protected readonly value: number;
        @Prop({ type: String, default: '' }) protected infoIconText: string;

        protected currentValue: number = NaN;

        /**
         * Change event handler.
         * @param {Event} event - Event object.
         */
        public change(event: Event) {
            this.setValue(Math.min(this.max, Math.max(this.min, Number((event.target as any).value))));
        }

        /**
         * Indicate if the value is increasable.
         * Return `true` if it is decreasable, else `false`.
         */
        protected get increasable(): boolean {
            return isNaN(this.currentValue) || this.currentValue < this.max;
        }

        /**
         * Indicate if the value is decreasable.
         * Return `true` if it is decreasable, else `false`.
         */
        protected get decreasable(): boolean {
            return isNaN(this.currentValue) || this.currentValue > this.min;
        }

        /**
         * Filter listeners.
         */
        protected get listeners(): { [key: string]: any } {
            const listeners: { [key: string]: any } = {...this.$listeners};

            delete listeners.change;

            return listeners;
        }

        /**
         * Paste event handler.
         * @param {Event} event - Event object.
         */
        protected paste(event) {
            if (!REGEXP_NUMBER.test(event.clipboardData.getData('text'))) {
                event.preventDefault();
            }
        }

        /**
         * Decrease the value.
         */
        protected decrease(): void {
            if (this.decreasable) {
                if (isNaN(this.currentValue)) {
                    this.currentValue = 0;
                }

                this.setValue(Math.min(this.max, Math.max(this.min, this.currentValue - this.step)));
            }
        }

        /**
         * Increase the value.
         */
        protected increase(): void {
            if (this.increasable) {
                if (isNaN(this.currentValue)) {
                    this.currentValue = 0;
                }

                this.setValue(Math.min(this.max, Math.max(this.min, this.currentValue + this.step)));
            }
        }

        /**
         * Set new value and dispatch change event.
         */
        protected setValue(value: number) {
            const oldValue = this.currentValue;
            let newValue = this.rounded ? Math.round(value) : value;

            if (this.min <= this.max) {
                newValue = Math.min(this.max, Math.max(this.min, newValue));
            }

            this.currentValue = newValue;
            this.$emit('change', newValue, oldValue);
            this.$nextTick(() => {
                // Change the input value when it is mounted
                if (this.$el && this.$refs.input instanceof HTMLInputElement) {
                    this.$refs.input.value = newValue.toString();
                }
            });
        }

        protected created(): void {
            this.setValue(this.value);

            this.$watch('value', (newValue: number) => {
                this.setValue(newValue);
            });
        }
    }
</script>
